import { useState, useEffect, useRef, useMemo } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

export default function useResizeObserver(ref, options = {}) {
  const {
    callback,
    shouldReturnValues = true,
    checkWidth = true,
    checkHeight = true
  } = options;

  const [size, setSize] = useState({
    width: ref.current?.offsetWidth ?? 0,
    height: ref.current?.offsetHeight ?? 0
  });

  const prevSize = useRef({ ...size });
  const hasInitialized = useRef(false);
  const observerRef = useRef(null);
  const didUnmount = useRef(false);

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    didUnmount.current = false;
    return () => (didUnmount.current = true);
  }, []);

  useEffect(() => {
    if (!observerRef.current) {
      observerRef.current = new ResizeObserver(entries => {
        if (!entries?.length) {
          return;
        }

        const entry = entries[0];
        const width = Math.round(entry.target.offsetWidth);
        const height = Math.round(entry.target.offsetHeight);

        if (
          !hasInitialized.current ||
          (checkWidth && prevSize.current.width !== width) ||
          (checkHeight && prevSize.current.height !== height)
        ) {
          hasInitialized.current = true;
          const newSize = { width, height };
          prevSize.current = newSize;

          if (callbackRef.current) {
            callbackRef.current(newSize, entry);
          }

          if (shouldReturnValues && !didUnmount.current) {
            setSize(newSize);
          }
        }
      });
    }

    const observer = observerRef.current;
    const el = ref.current;

    if (el) observer.observe(el);

    return () => {
      if (el) observer?.unobserve(el);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReturnValues, ref.current, checkWidth, checkHeight]);

  const sizeValue = useMemo(() => {
    return { width: size.width, height: size.height };
  }, [size.width, size.height]);

  return shouldReturnValues ? sizeValue : null;
}
